import { ColDef, ColGroupDef, RowDoubleClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import {  defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { PRD3050_R01Dto, defaultProductResultData, GridRowData } from "./Dto_PRD3050_R01";
import { usePRD3050_R01Store } from "./Store_PRD3050_R01";

import useUiuxStore from "src/store/useUiuxStore";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'PRD3050_R01-grid-column-state';

interface Props {
    gridRef: React.RefObject<AgGridReact<PRD3050_R01Dto> | null>;
}

export const Table_PRD3050_R01 = (props: Props) => {

    const { gridRef } = props;

    const { 
        PRD3050_R01List,
        selectedPRD3050_R01List, setSelectedPRD3050_R01List, initSelectedPRD3050_R01List,
        
        columnState, setColumnState,
        scrollTop, setScrollTop,
    } = usePRD3050_R01Store();

	const { addTabList, setActiveTab } = useUiuxStore();
	const { faultyValueList } = useCommonCodeStore();

    const {
        createRowClassRules,
        ensureSelectedRow,
        settingRowIndex,
    } = useGridSettingContent({
        gridRef,
        key: 'valueIndex',
        gridData: PRD3050_R01List || [],
        defaultGridData: defaultProductResultData as GridRowData,
        selectedRow: selectedPRD3050_R01List,
    });
    const getRowIdForGrid = useCallback((params: any) => {
        const id = params.data?.valueIndex || params.id;
        return String(id);
    }, []);
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    const headerNames: Record<string, string> = {
        rowIndex: '',
        
    };

    const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

	const getBadRate = useCallback((data?: PRD3050_R01Dto) => {
		if (data?.badRate != null && data.badRate !== 0) return Number(data.badRate);
		const productQty = Number(data?.productQty) || 0;
		const badQty = Number(data?.badQty) || 0;
		if (productQty <= 0 || badQty < 0) return 0;
		return (badQty / productQty) * 100;
	}, []);

	const createBadRankGroup = useCallback((
		headerName: string,
		caseField: 'firstCase' | 'secondCase' | 'thirdCase',
		qtyField: 'firstCaseQty' | 'secondCaseQty' | 'thirdCaseQty',
		rateField: 'firstCaseRate' | 'secondCaseRate' | 'thirdCaseRate',
	): ColGroupDef<PRD3050_R01Dto> => ({
		headerName,
		headerClass: 'common-group-header',
		marryChildren: true,
		children: [
			{
				headerName: '불량사유', colId: caseField, field: caseField, width: 130,
				editable: false,
				cellStyle: (p) => getCellStyle(p, caseField),
				...CommonCodeColumn.options(faultyValueList, false, undefined, undefined),
			},
			{
				headerName: '불량수량', colId: qtyField, field: qtyField, width: 90,
				editable: false,
				type: 'numericColumn',
				cellStyle: (p) => getCellStyle(p, qtyField),
				valueFormatter: (p) => p.data?.[caseField] ? (p.value ?? 0).toLocaleString() : '',
			},
			{
				headerName: '불량률', colId: rateField, field: rateField, width: 90,
				editable: false,
				type: 'numericColumn',
				cellStyle: (p) => getCellStyle(p, rateField),
				valueFormatter: (p) => p.data?.[caseField] ? `${(Number(p.value) || 0).toFixed(1)}%` : '',
			},
		],
	}), [faultyValueList, getCellStyle]);

    const baseColumnDefs: (ColDef<PRD3050_R01Dto> | ColGroupDef<PRD3050_R01Dto>)[] = useMemo(() => [
            ...getDefaultRowIndexColumnConfig(),
			{
				headerName: '회사명', colId: 'compNm', field: 'compNm', width: 140,
				editable: false,
				cellStyle: (p) => getCellStyle(p, 'compNm'),
			},
			{
				headerName: '공정명', colId: 'workplaceNm', field: 'workplaceNm', width: 160,
				editable: false,
				cellStyle: (p) => getCellStyle(p, 'workplaceNm'),
			},
			{
				headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 150,
				editable: false,
				cellStyle: (p) => getCellStyle(p, 'itemCd'),
			},
			{
				headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 200,
				editable: false,
				cellStyle: (p) => getCellStyle(p, 'itemNm'),
			},
			{
				headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 160,
				editable: false,
				cellStyle: (p) => getCellStyle(p, 'itemSpec'),
			},
			{
				headerName: '생산량', colId: 'productQty', field: 'productQty', width: 100,
				editable: false,
				type: 'numericColumn',
				cellStyle: (p) => getCellStyle(p, 'productQty'),
				valueFormatter: (p) => (p.value ?? 0).toLocaleString(),
			},
			{
				headerName: '불량합계',
				headerClass: 'common-group-header',
				marryChildren: true,
				children: [
					{
						headerName: '수량', colId: 'badQty', field: 'badQty', width: 90,
						editable: false,
						type: 'numericColumn',
						cellStyle: (p) => getCellStyle(p, 'badQty'),
						valueFormatter: (p) => (p.value ?? 0).toLocaleString(),
					},
					{
						headerName: '불량률(%)', colId: 'badRate', field: 'badRate', width: 80,
						editable: false,
						type: 'numericColumn',
						cellStyle: (p) => getCellStyle(p, 'badRate'),
						valueGetter: (params) => getBadRate(params.data),
						valueFormatter: (p) => `${(Number(p.value) || 0).toFixed(1)}%`,
					},
				],
			},
			createBadRankGroup('1순위', 'firstCase', 'firstCaseQty', 'firstCaseRate'),
			createBadRankGroup('2순위', 'secondCase', 'secondCaseQty', 'secondCaseRate'),
			createBadRankGroup('3순위', 'thirdCase', 'thirdCaseQty', 'thirdCaseRate'),
			{
				headerName: '기타',
				headerClass: 'common-group-header',
				marryChildren: true,
				children: [
					{
						headerName: '불량사유', colId: 'otherCaseNm', field: 'otherCaseNm', width: 130,
						editable: false,
						cellStyle: (p) => getCellStyle(p, 'otherCaseNm'),
						valueGetter: (params) => {
							const data = params.data;
							if (!data?.otherCaseQty) return '';
							const name = data.otherCaseNm || '기타';
							return data.otherCaseCount ? `${name} ${data.otherCaseCount}건` : name;
						},
					},
					{
						headerName: '불량수량', colId: 'otherCaseQty', field: 'otherCaseQty', width: 90,
						editable: false,
						type: 'numericColumn',
						cellStyle: (p) => getCellStyle(p, 'otherCaseQty'),
						valueFormatter: (p) => p.data?.otherCaseQty ? (p.value ?? 0).toLocaleString() : '',
					},
					{
						headerName: '불량률', colId: 'otherCaseRate', field: 'otherCaseRate', width: 90,
						editable: false,
						type: 'numericColumn',
						cellStyle: (p) => getCellStyle(p, 'otherCaseRate'),
						valueFormatter: (p) => p.data?.otherCaseQty ? `${(Number(p.value) || 0).toFixed(1)}%` : '',
					},
				],
			},
    ], [createBadRankGroup, getBadRate, getCellStyle]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs as ColDef<PRD3050_R01Dto>[], [])
    , [baseColumnDefs]);

    const columnDefs: (ColDef<GridRowData> | ColGroupDef<GridRowData>)[] = useMemo(() => {
        if (columnState === null) {
            return baseColumnDefs;
        }
        return GridCustomColumnDefs<PRD3050_R01Dto>({
            baseColumnDefs,
            columnState,
            headerNames,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

    const getRowStyleForPUR2020 = useCallback((params: any) => {
        if (params.node.rowPinned === 'bottom') {
            return { background: '#F0F0F0', fontWeight: 'bold', borderTop: 'none' };
        }
        return undefined;
    }, []);

    const shiftRangeSelectionConfig = useMemo(() => ({
        enableShiftRangeSelect: true,
        idKey: 'inspectNo' as const,
        selectedIds: selectedPRD3050_R01List as Array<string | number>,
        setSelectedIds: (ids: Array<string | number>) => setSelectedPRD3050_R01List(ids.map(String)),
    }), [selectedPRD3050_R01List, setSelectedPRD3050_R01List]);

	
	// 더블클릭으로 KSK2020_R01 페이지로 이동
	const handleRowDoubleClick = useCallback((e: RowDoubleClickedEvent<PRD3050_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
	}, [addTabList, setActiveTab]);

    const pinnedBottomRow = useMemo(() => {
        const detailRows = PRD3050_R01List;
		const productQty = detailRows.reduce((sum, row) => sum + (Number(row.productQty) || 0), 0);
		const badQty = detailRows.reduce((sum, row) => sum + (Number(row.badQty) || 0), 0);

        return [{
            compNm: `${detailRows.length.toLocaleString()}건`,
            productQty,
            badQty,
            badRate: productQty > 0 ? (badQty / productQty) * 100 : 0,
        }];
    }, [PRD3050_R01List]);

    useEffect(() => { ensureSelectedRow(); }, [PRD3050_R01List, selectedPRD3050_R01List]);

    // 복합키 기반 rowClassRules
    const rowClassRules = useMemo(() => ({
        'selected': (params: any) => {
            if (!params.data) return false;
            // const compositeKey = params.data.valueIndex;
            return selectedPRD3050_R01List.includes(params.data.inspectNo);
        }
    }), [selectedPRD3050_R01List]);

    const handleRowDragEnd = useCallback((e: any) => {
        const api = gridRef.current?.api || e?.api;
        if (!api) return;
        
        if (api.applyColumnState) {
            api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
        }
        
        const reordered: GridRowData[] = [];
        api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
            if (!node?.data) return;
            reordered.push({
                ...node.data,
                orderIndex: idx + 1,
                orderIndexSeq: 0,
            } as GridRowData);
        });
        // 드래그 후 리스트 자체는 유지 (필요시 상태 업데이트 로직 추가)
    }, [gridRef]);

    return (
        <div className="flex flex-col gap-3 h-full min-h-0">
            <GridContextWrapper<GridRowData>
            	enableCellCalculation
                gridRef={gridRef}
                paddedGridData={PRD3050_R01List}
                initSelected={initSelectedPRD3050_R01List}
				defaultRowData={defaultProductResultData}
                baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
                defaultColumnState={defaultColumnState}
                setColumnState={setColumnState}
                localStorageKey={COLUMN_STATE_KEY}
                menuItems={undefined}
            >
                <Grid_Select<GridRowData>
                    ref={gridRef}
                    rowData={PRD3050_R01List}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    getRowId={getRowIdForGrid}
                    onCellKeyDown={undefined}
					setHoveredRowIndex={setHoveredRowIndex}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setColumnState}
                    onGridReady={(params) => {
                        params.api.getDisplayedRowCount();
                        params.api.getRenderedNodes();
                    }}
                    shiftRangeSelection={shiftRangeSelectionConfig}
					onRowDoubleClicked={handleRowDoubleClick}
                    pinnedBottomRowData={pinnedBottomRow}
                    rowClassRules={rowClassRules}
                    getRowStyle={getRowStyleForPUR2020}
                    rowDragManaged={true}
                    rowDragEntireRow={true}
                    onRowDragEnd={handleRowDragEnd}
                    isRowSelectable={(node) => !!node.data}
                    suppressRowClickSelection={false}
                    ensureDomOrder={true}
                    scrollTop={scrollTop}
                    setScrollTop={setScrollTop}
                />
            </GridContextWrapper>
        </div>
    );
}


